import React, { useCallback } from "react";
import { graphql, useMutation } from "react-relay";
import { useNavigate, useOutletContext, useSearchParams } from "react-router";
import type { ChatAttachmentType } from "src/components/ui/chat/useChatAttachments";
import { useMe } from "src/context/MeContext";
import { useTeamSelection } from "src/hooks/use-team-selection";

import ChatArea from "../../AskPage/components/ChatArea";
import { AskPageOutletContext } from "../../AskPage/types";
import type { EmptyChatsCreateConversationMutation } from "./__generated__/EmptyChatsCreateConversationMutation.graphql";
import type { EmptyChatsCreateOnboardingTaskStatusMutation } from "./__generated__/EmptyChatsCreateOnboardingTaskStatusMutation.graphql";

const createConversationMutation = graphql`
  mutation EmptyChatsCreateConversationMutation($input: SendMessageInput!) {
    sendMessage(input: $input) {
      llmConversation {
        id
      }
    }
  }
`;

interface SendMessageInputType {
  message: string;
  attachments?: { handle: string }[];
}

const createOnboardingTaskStatusMutation = graphql`
  mutation EmptyChatsCreateOnboardingTaskStatusMutation($input: CreateOnboardingTaskStatusInput!) {
    createOnboardingTaskStatus(input: $input) {
      taskStatus {
        id
        taskId
        status
      }
    }
  }
`;

// Component for empty chat state (when no conversation is selected)
const EmptyChats: React.FC = () => {
  const { userData, messagesEndRef, tips, refetchSidebar } = useOutletContext<AskPageOutletContext>();
  const [searchParams] = useSearchParams();
  const prefilledQuestion = searchParams.get("question") || "";
  const onboardingStep = searchParams.get("onboarding");
  const isOnboarding = onboardingStep !== null;

  const me = useMe();
  const { teamId } = useTeamSelection({ userId: me.id, initialTeamContext: me.teamContext });

  const navigate = useNavigate();
  const [commitMutation, isSending] = useMutation<EmptyChatsCreateConversationMutation>(createConversationMutation);
  const [commitCreateTaskStatus] = useMutation<EmptyChatsCreateOnboardingTaskStatusMutation>(
    createOnboardingTaskStatusMutation
  );

  const sendMessage = useCallback(
    async (message: string, attachments: ChatAttachmentType[] = []) => {
      const trimmedMessage = message.trim();
      if (!trimmedMessage) return;

      const attachmentRefs = attachments
        .map((a) => a.handle)
        .filter((h): h is string => Boolean(h))
        .map((handle) => ({ handle }));

      const input: SendMessageInputType = {
        message: trimmedMessage,
        ...(attachmentRefs.length ? { attachments: attachmentRefs } : {}),
      };

      return new Promise<void>((resolve, reject) => {
        commitMutation({
          variables: { input },
          onCompleted: (response) => {
            // Refetch sidebar to show new conversation
            refetchSidebar();

            // Mark onboarding task as completed when first message is sent
            if (isOnboarding && teamId) {
              commitCreateTaskStatus({
                variables: {
                  input: {
                    teamId,
                    taskId: "CHAT_WITH_TIERZERO",
                    status: "COMPLETED"
                  }
                }
              });
            }

            // Use llmConversation ID for /chat route
            const llmConversationId = response.sendMessage.llmConversation?.id;
            if (llmConversationId) {
              // Navigate with state to show initial message was sent
              // Preserve onboarding parameter if present
              const url = onboardingStep
                ? `/chat/c/${llmConversationId}?onboarding=${onboardingStep}`
                : `/chat/c/${llmConversationId}`;
              navigate(url, {
                state: { initialMessage: trimmedMessage, isNewConversation: true },
              });
            }
            resolve();
          },
          onError: (error) => {
            reject(error);
          },
        });
      });
    },
    [commitMutation, commitCreateTaskStatus, isOnboarding, navigate, onboardingStep, refetchSidebar, teamId]
  );

  return (
    <ChatArea
      messagesEndRef={messagesEndRef}
      onSendMessage={sendMessage}
      isSendingMessage={isSending}
      userData={userData}
      tips={tips}
      prefilledQuestion={prefilledQuestion}
    />
  );
};

export default EmptyChats;

